import type { ScheduleItem } from '../types/index.js'
import { findGridGaps, type GridGap } from './grid-gaps.js'

/**
 * Portée maximale de la « nuit » après minuit d'antenne. Même valeur que
 * `NIGHT_LOOKAHEAD_HOURS` côté serveur : au-delà, la chaîne n'émet plus, et
 * le trou n'est pas une fin de journée à combler.
 */
export const NIGHT_LOOKAHEAD_HOURS = 6

/**
 * Prolongement de la journée affichée jusqu'au premier élément du lendemain.
 *
 * La journée d'antenne s'arrête à minuit, mais l'antenne, elle, continue : un
 * infoservice qui comble la fin de soirée doit pouvoir aller jusqu'au premier
 * programme, jingle ou pub du lendemain. La grille affiche donc cette queue de
 * nuit, avec les passages d'infoservice qui s'y trouvent déjà.
 */
export interface NightTail {
  /** Fin de l'affichage : début de `nextItem`, sinon fin de ce qui passe minuit. */
  endMs: number
  /** Passages d'infoservice qui commencent après minuit, avant `nextItem`. */
  items: ScheduleItem[]
  /** Premier élément du lendemain qui n'est pas un infoservice. */
  nextItem: ScheduleItem | null
}

/**
 * @param dayItems  éléments de la journée (ceux qui passent minuit compris)
 * @param lookahead éléments lus après minuit, sur `NIGHT_LOOKAHEAD_HOURS`
 */
export function computeNightTail(
  dayItems: ScheduleItem[],
  lookahead: ScheduleItem[],
  dayEndMs: number,
): NightTail {
  const maxEndMs = dayEndMs + NIGHT_LOOKAHEAD_HOURS * 3_600_000
  const candidates = lookahead
    .filter(item => item.status !== 'SKIPPED' && new Date(item.startTime).getTime() >= dayEndMs)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())

  const nextItem = candidates.find(item => item.itemType !== 'INFOSERVICE') ?? null
  const nextStartMs = nextItem ? new Date(nextItem.startTime).getTime() : Infinity
  const items = candidates.filter(item =>
    item.itemType === 'INFOSERVICE' && new Date(item.startTime).getTime() < nextStartMs)

  let endMs = nextItem ? nextStartMs : dayEndMs
  if (!nextItem) {
    for (const item of [...dayItems, ...items]) {
      if (item.status === 'SKIPPED') continue
      endMs = Math.max(endMs, new Date(item.endTime).getTime())
    }
  }
  return { endMs: Math.min(Math.max(endMs, dayEndMs), maxEndMs), items, nextItem }
}

/**
 * Trous de la journée affichée, fin de soirée comprise.
 *
 * Le trou entre le dernier élément de la journée et le premier du lendemain
 * appartient à la journée où il **commence** : il est proposé ici, et la
 * journée suivante ne le voit pas (aucun élément précédent n'y est chargé).
 * Les trous qui commencent après minuit restent au lendemain. Un trou qui
 * passe minuit est posé dans la grille de la journée affichée, pas dans celle
 * de la semaine suivante que porterait le premier élément du lundi.
 */
export function findDayGaps(
  items: ScheduleItem[],
  tail: Pick<NightTail, 'nextItem'> | null,
  dayEndMs: number,
  dayGridId: string | null,
): GridGap[] {
  const gaps = findGridGaps(tail?.nextItem ? [...items, tail.nextItem] : items)
  return gaps
    .filter(gap => new Date(gap.startTime).getTime() <= dayEndMs)
    .map(gap => (dayGridId && new Date(gap.endTime).getTime() > dayEndMs
      ? { ...gap, gridId: dayGridId }
      : gap))
}
